import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import LanguageSwitcher from "./LanguageSwitcher";

export default function Header({ onLoginClick, user, logout }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const go = (path) => {
    navigate(path);
    setOpen(false);
  };

  const handleCreateReport = () => {
    if (!user) {
      setOpen(false);
      onLoginClick?.();
      return;
    }
    go("/report");
  };

  return (
    <header className="w-full bg-white/90 backdrop-blur border-b sticky top-0 z-50">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
        <div className="flex items-center justify-between">
          <div
            className="flex items-center space-x-2 cursor-pointer shrink-0"
            onClick={() => go("/")}
          >
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white font-bold text-lg">
              FM
            </div>
            <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
              FixOurIndia
            </span>
          </div>

          {/* Desktop menu */}
          <div className="hidden md:flex items-center gap-3">
            <div className="flex items-center gap-0.5">
            <button
                onClick={handleCreateReport}
                className="p-1 rounded hover:bg-gray-100 text-gray-700 hover:text-cyan-600"
                aria-label="Create new report"
                title="Create new report"
                data-testid="header-create-report-plus"
              >
                <Plus size={14} />
              </button>
              <button
                onClick={() => go("/reports")}
                className="text-gray-700 hover:text-cyan-600 font-medium"
              >
                {t("nav.viewReports")}
              </button>
            </div>

            <button
              onClick={() => go("/about")}
              className="text-gray-700 hover:text-cyan-600 font-medium"
            >
              {t("nav.about")}
            </button>

            {user ? (
              <>
                {user.role === "moderator" && (
                  <button
                    onClick={() => go("/moderator/dashboard")}
                    className="text-gray-700 hover:text-cyan-600 font-medium"
                  >
                    {t("nav.moderator")}
                  </button>
                )}

                {(user.role === "admin" || user.role === "super_admin") && (
                  <button
                    onClick={() => go("/admin")}
                    className="text-gray-700 hover:text-cyan-600 font-medium"
                  >
                    {t("nav.admin")}
                  </button>
                )}

                <button
                  onClick={() => go("/dashboard")}
                  className="text-gray-700 hover:text-cyan-600 font-medium"
                >
                  {t("nav.dashboard")}
                </button>

                <button onClick={logout} className="btn-secondary shrink-0">
                  {t("nav.logout")}
                </button>
              </>
            ) : (
              <button onClick={onLoginClick} className="btn-primary">
                {t("nav.login")}
              </button>
            )}

            <LanguageSwitcher />
          </div>

          {/* Mobile */}
          <div className="md:hidden flex items-center gap-2">
            <LanguageSwitcher />
            <button
              onClick={() => setOpen(!open)}
              className="px-3 py-2 rounded-lg border text-gray-700"
              aria-label="Open menu"
            >
              ☰
            </button>
          </div>
        </div>

        {open && (
          <div className="md:hidden mt-3 rounded-xl border bg-white p-3 flex flex-col gap-2">
            {/* View Reports row + plus icon on right */}
            <div className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-gray-50">
              <button
                onClick={() => go("/reports")}
                className="text-left text-gray-800 flex-1"
              >
                {t("nav.viewReports")}
              </button>

              <button
                onClick={handleCreateReport}
                className="p-2 rounded-md hover:bg-gray-100 text-gray-700 hover:text-cyan-600"
                aria-label="Create new report"
                title="Create new report"
                data-testid="header-create-report-plus-mobile"
              >
                <Plus size={16} />
              </button>
            </div>

            <button
              onClick={() => go("/about")}
              className="text-left px-3 py-2 rounded-lg hover:bg-gray-50"
            >
              {t("nav.about")}
            </button>

            {user ? (
              <>
                {user.role === "moderator" && (
                  <button
                    onClick={() => go("/moderator/dashboard")}
                    className="text-left px-3 py-2 rounded-lg hover:bg-gray-50"
                  >
                    {t("nav.moderator")}
                  </button>
                )}

                {(user.role === "admin" || user.role === "super_admin") && (
                  <button
                    onClick={() => go("/admin")}
                    className="text-left px-3 py-2 rounded-lg hover:bg-gray-50"
                  >
                    {t("nav.admin")}
                  </button>
                )}

                <button
                  onClick={() => go("/dashboard")}
                  className="text-left px-3 py-2 rounded-lg hover:bg-gray-50"
                >
                  {t("nav.dashboard")}
                </button>

                <button
                  onClick={logout}
                  className="text-left px-2 py-1 rounded-lg hover:bg-gray-50 text-red-600"
                >
                  Logout
                </button>
              </>
            ) : (
              <button
                onClick={() => {
                  setOpen(false);
                  onLoginClick?.();
                }}
                className="text-left px-3 py-2 rounded-lg hover:bg-gray-50"
              >
                {t("nav.login")}
              </button>
            )}
          </div>
        )}
      </nav>
    </header>
  );
}
